import { IconChecks, IconBellOff } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useNotificationContext } from "@/contexts/NotificationContext";
import type { NotificationItem } from "@/lib/api";

const TYPE_LABEL: Record<string, string> = {
	quiz_reminder: "Quiz",
	partner_answered: "Partner",
	streak_risk: "Streak",
	system: "System",
};

function resolveNotificationTarget(notif: NotificationItem): string | null {
	if (notif.data) {
		try {
			const data = JSON.parse(notif.data);
			if (data.bothAnswered) return "/result";
			if (data.coupleId && notif.type === "partner_answered") return "/quiz";
			if (data.coupleId && notif.type === "streak_risk") return "/quiz";
			if (data.coupleId && notif.type === "quiz_reminder") return "/quiz";
			if (data.score !== undefined) return "/result";
		} catch { /* ignore */ }
	}
	if (notif.type === "partner_answered") return "/quiz";
	if (notif.type === "quiz_reminder") return "/quiz";
	if (notif.type === "streak_risk") return "/quiz";
	if (notif.title?.includes("Jurnal")) return "/journals";
	if (notif.title?.includes("Mood") || notif.title?.includes("mood")) return "/moods";
	if (notif.title?.includes("bergabung")) return "/dashboard";
	if (notif.title?.includes("Kuis") || notif.title?.includes("Hasil")) return "/result";
	return null;
}

function timeAgo(ts: number): string {
	const diff = Date.now() - ts;
	if (diff < 60_000) return "Baru saja";
	if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m lalu`;
	if (diff < 86400_000) return `${Math.floor(diff / 3600_000)}j lalu`;
	return `${Math.floor(diff / 86400_000)}h lalu`;
}

function NotificationRow({
	notif,
	onRead,
}: {
	notif: NotificationItem;
	onRead: (id: string) => void;
}) {
	const navigate = useNavigate();
	const unread = !notif.readAt;

	const handleClick = () => {
		if (unread) onRead(notif.id);
		const target = resolveNotificationTarget(notif);
		if (target) navigate(target);
	};

	return (
		<button
			type="button"
			onClick={handleClick}
			className="flex w-full text-left border-none cursor-pointer p-3 gap-3 items-start border-b"
			style={{
				background: unread ? "var(--color-primary-muted, rgba(236,72,153,0.06))" : "transparent",
				borderColor: "var(--color-border)",
			}}
		>
			<div className="flex-1 min-w-0">
				<div className="flex items-center gap-2">
					<span className="text-[0.7rem] font-semibold px-1.5 py-px rounded" style={{ color: "var(--color-primary)", background: "var(--color-primary-muted, rgba(236,72,153,0.1))" }}>
						{TYPE_LABEL[notif.type] ?? notif.type}
					</span>
					{unread && (
						<span className="w-[7px] h-[7px] rounded-full shrink-0" style={{ background: "var(--color-primary)" }} />
					)}
				</div>
				<div className="text-[0.88rem] mt-1 leading-tight" style={{ fontWeight: unread ? 600 : 400, color: "var(--color-text)" }}>
					{notif.title}
				</div>
				<div className="text-[0.78rem] mt-0.5 leading-tight" style={{ color: "var(--color-text-secondary, #6b7280)" }}>
					{notif.body}
				</div>
			</div>
			<div className="text-[0.7rem] whitespace-nowrap shrink-0" style={{ color: "var(--color-text-secondary, #9ca3af)" }}>
				{timeAgo(notif.createdAt)}
			</div>
		</button>
	);
}

export function NotificationPanel() {
	const { items, unreadCount, loading, error, markRead, markAllRead } = useNotificationContext();

	return (
		<div className="notification-panel flex flex-col overflow-hidden rounded-xl" style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)", boxShadow: "0 8px 30px rgba(0,0,0,0.12)", maxHeight: 420 }}>
			<div className="flex justify-between items-center px-4 py-3" style={{ borderBottom: "1px solid var(--color-border)" }}>
				<span className="font-bold text-[0.95rem]">Notifikasi</span>
				{unreadCount > 0 && (
					<button type="button" onClick={() => markAllRead()} className="flex items-center gap-1 cursor-pointer border-none bg-transparent text-[0.78rem] font-semibold p-0" style={{ color: "var(--color-primary)" }}>
						<IconChecks size={14} />
						Tandai semua dibaca
					</button>
				)}
			</div>
			<div className="flex-1 overflow-y-auto">
				{loading && items.length === 0 ? (
					<div className="p-8 text-center text-[0.85rem]" style={{ color: "var(--color-text-secondary, #9ca3af)" }}>
						Memuat...
					</div>
				) : error ? (
					<div className="p-8 text-center text-[0.85rem]" style={{ color: "#ef4444" }}>
						{error}
					</div>
				) : items.length === 0 ? (
					<div className="p-8 text-center text-[0.85rem] flex flex-col items-center" style={{ color: "var(--color-text-secondary, #9ca3af)" }}>
						<IconBellOff size={32} className="opacity-40 mb-2" />
						<div>Belum ada notifikasi</div>
					</div>
				) : (
					<ul className="list-none p-0 m-0">
						{items.map((n) => (
							<li key={n.id}>
								<NotificationRow notif={n} onRead={markRead} />
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
}
